import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'dark' | 'outline' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-lime text-ink hover:bg-[#e4ff82] disabled:hover:bg-lime',
    dark: 'bg-ink text-foreground hover:bg-[#1c3125]',
    outline: 'border border-line text-foreground hover:border-lime/60 hover:text-lime',
    ghost: 'text-muted-foreground hover:bg-lime/10 hover:text-lime',
    danger: 'border border-coral/40 text-coral hover:bg-coral/10'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-8 gap-1.5 px-3 text-[10px]',
    md: 'h-10 gap-2 px-4 text-[11px]',
    lg: 'h-12 gap-3 px-5 text-xs'
}

export const buttonClass = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => {
    return cn(
        'inline-flex shrink-0 items-center justify-center font-mono font-medium uppercase tracking-[0.06em] whitespace-nowrap transition duration-200 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50 disabled:hover:translate-y-0',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    children: ReactNode
}

export const Button: FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    className,
    type = 'button',
    children,
    ...rest
}): ReactNode => {
    return (
        <button type={type} className={buttonClass(variant, size, className)} {...rest}>
            {children}
        </button>
    )
}
